import React from 'react';
import { Users, Headphones, Sparkles, Flame, CalendarDays } from 'lucide-react';
import { StarRating } from './Reviews';

/**
 * Compact hero strip shown above the fold on TourDetailPage.
 * Displays rating + popularity counters + "X people started today".
 */
const TrustSignals = ({ trust, className = '' }) => {
  if (!trust) return null;

  const {
    average_rating = 0,
    review_count = 0,
    explorers = 0,
    listeners_this_month = 0,
    started_today = 0,
    total_starts = 0,
    is_new_tour = false,
    featured = false,
  } = trust;

  const formatCount = (n) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K+`;
    return String(n);
  };

  return (
    <div className={`space-y-3 ${className}`} data-testid="trust-signals">
      {/* Top tags row */}
      <div className="flex flex-wrap items-center gap-2">
        {is_new_tour && (
          <span
            data-testid="badge-new-tour"
            className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-400 text-amber-900 text-xs font-black uppercase tracking-wide rounded-full shadow-sm"
          >
            <Sparkles className="w-3.5 h-3.5" />
            New Tour
          </span>
        )}
        {!is_new_tour && total_starts >= 50 && (
          <span
            data-testid="badge-trending"
            className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-500 text-white text-xs font-black uppercase tracking-wide rounded-full shadow-sm"
          >
            <Flame className="w-3.5 h-3.5" />
            Trending
          </span>
        )}
        {featured && (
          <span
            data-testid="badge-featured"
            className="inline-flex items-center gap-1.5 px-3 py-1 bg-cyan-500 text-white text-xs font-black uppercase tracking-wide rounded-full shadow-sm"
          >
            Featured
          </span>
        )}
        {review_count > 0 && (
          <div
            className="inline-flex items-center gap-2 bg-white/90 px-3 py-1 rounded-full shadow-sm"
            data-testid="trust-rating-pill"
          >
            <StarRating rating={Math.round(average_rating)} size="sm" />
            <span className="font-bold text-gray-900 text-sm">{average_rating}</span>
            <span className="text-gray-600 text-xs">
              ({review_count} {review_count === 1 ? 'review' : 'reviews'})
            </span>
          </div>
        )}
      </div>

      {/* Popularity counter row */}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {explorers > 0 && (
          <div
            data-testid="trust-explorers"
            className="inline-flex items-center gap-2 bg-white/90 border border-cyan-100 px-3 py-1.5 rounded-full shadow-sm"
          >
            <Users className="w-4 h-4 text-cyan-600" />
            <span className="font-bold text-gray-900">{formatCount(explorers)}</span>
            <span className="text-gray-600">Explorers joined</span>
          </div>
        )}
        {listeners_this_month > 0 && (
          <div
            data-testid="trust-listeners"
            className="inline-flex items-center gap-2 bg-white/90 border border-blue-100 px-3 py-1.5 rounded-full shadow-sm"
          >
            <Headphones className="w-4 h-4 text-blue-600" />
            <span className="font-bold text-gray-900">{formatCount(listeners_this_month)}</span>
            <span className="text-gray-600">listening this month</span>
          </div>
        )}
        {started_today > 0 && (
          <div
            data-testid="trust-today"
            className="inline-flex items-center gap-2 bg-white/90 border border-amber-100 px-3 py-1.5 rounded-full shadow-sm animate-pulse-subtle"
          >
            <CalendarDays className="w-4 h-4 text-amber-600" />
            <span className="font-bold text-gray-900">{started_today}</span>
            <span className="text-gray-600">
              {started_today === 1 ? 'person' : 'people'} started today
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrustSignals;
